import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, MoreVertical, Undo2 } from 'lucide-react';
import {
  stageMoveOptions,
  stagesForGarment,
  type KanbanCard,
  type StageConfigurationView,
  type WorkflowStage,
} from '@tailoring-crm/shared';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@tailoring-crm/ui';

/**
 * The PRIMARY way a garment is moved, and the only one that works on a phone.
 *
 * Dragging a card between columns is a laptop convenience. On the 360px screen this product is
 * mostly used on, columns are a horizontal scroll and dragging between them is somewhere between
 * awkward and impossible — so every card carries its own control: one button that advances it,
 * and a menu for going back. The board is fully operable without ever dragging anything.
 *
 * What the control offers comes from `stageMoveOptions` — the same shared function the API
 * validates against — so the board can never present a move the server would refuse.
 */
export interface StageControlProps {
  card: KanbanCard;
  configuration: StageConfigurationView | undefined;
  /** False for a role that may not send work back; the rework menu is then hidden entirely. */
  canRevert?: boolean;
  onAdvance: (toStage: WorkflowStage) => void;
  /** Backward moves need a reason, so they open a dialog rather than firing immediately. */
  onRevert: (toStage: WorkflowStage) => void;
  disabled?: boolean;
}

export function StageControl({
  card,
  configuration,
  canRevert = true,
  onAdvance,
  onRevert,
  disabled = false,
}: StageControlProps): ReactElement | null {
  const { t } = useTranslation();

  /**
   * THE GARMENT'S OWN ROUTE, not the shop's whole workflow.
   *
   * A shop that runs ironing for its trousers and not for its blouses has two different
   * answers to "what comes after finishing", and offering the shop-wide one here would put a
   * button on the card that the API then refuses. Falls back to the shop's workflow for a
   * garment type nobody customised, which is most of them.
   */
  const enabled = stagesForGarment(configuration, card.garmentTypeKey);
  const { advanceTo, revertTo } = stageMoveOptions(card.stage, enabled);
  const labelOf = (stage: WorkflowStage): string =>
    configuration?.stages.find((one) => one.key === stage)?.label ?? t(`stage.${stage}`);

  if (!advanceTo && (!canRevert || revertTo.length === 0)) return null;

  return (
    <div className="flex items-center gap-1">
      {advanceTo ? (
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => onAdvance(advanceTo)}
          aria-label={t('kanban.advanceTo', {
            garment: card.garmentTypeLabel,
            stage: labelOf(advanceTo),
          })}
        >
          {labelOf(advanceTo)}
          <ChevronRight aria-hidden="true" />
        </Button>
      ) : null}

      {canRevert && revertTo.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="size-9"
              disabled={disabled}
              aria-label={t('kanban.moveMenu', { garment: card.garmentTypeLabel })}
            >
              <MoreVertical aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{t('kanban.sendBack')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {revertTo.map((stage) => (
              <DropdownMenuItem key={stage} onSelect={() => onRevert(stage)}>
                <Undo2 aria-hidden="true" className="mr-2 size-4" />
                {labelOf(stage)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
